import { useEffect, useState, type FormEvent } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import type { ApiAcessoPublico } from '../../application/portas';
import { Marca } from '../design-system/Marca';

type Props = { token: string | null; api: ApiAcessoPublico };

const SUPORTE = 'suporte@nexital.com.br';

/** E-mail e nome confirmados pelo Google (quando o formulário veio do login). */
function useDadosDoGoogle(token: string | null, api: ApiAcessoPublico) {
  const [dados, definirDados] = useState<{ email: string; nome: string | null } | null>(null);
  useEffect(() => {
    if (token) api.solicitacaoPreenchida(token).then(definirDados, () => undefined);
  }, [api, token]);
  return dados;
}

/**
 * Acesso mediante convite: quem entrou com o Google sem conta nem convite chega aqui com o e-mail já confirmado e pode
 * deixar os dados para contato e negociação (o suporte recebe por e-mail).
 */
export function TelaSolicitarAcesso({ token, api }: Props) {
  const google = useDadosDoGoogle(token, api);
  const [campos, definirCampos] = useState({ nome: '', email: '', telefone: '', empresa: '' });
  const [situacao, definirSituacao] = useState<'preenchendo' | 'enviando' | 'enviado'>('preenchendo');
  const [erro, definirErro] = useState<string>();
  useEffect(() => {
    if (google) definirCampos((c) => ({ ...c, email: google.email, nome: c.nome || google.nome || '' }));
  }, [google]);
  const mudar = (campo: keyof typeof campos) => (valor: string) => definirCampos((c) => ({ ...c, [campo]: valor }));

  const aoEnviar = async (evento: FormEvent) => {
    evento.preventDefault();
    definirSituacao('enviando');
    definirErro(undefined);
    try {
      await api.solicitarAcesso({ ...campos, token: google && token ? token : undefined });
      definirSituacao('enviado');
    } catch (e) {
      definirErro((e as Error).message);
      definirSituacao('preenchendo');
    }
  };

  if (situacao === 'enviado') return <PedidoRecebido nome={campos.nome} />;
  const { nome, email, telefone, empresa } = campos;
  const completo = nome.trim().length >= 2 && email.includes('@') && telefone.trim().length >= 8 && empresa.trim().length >= 2;
  return (
    <main className="tela-acesso">
      <form className="cartao-acesso cartao-acesso--largo" onSubmit={aoEnviar} noValidate>
        <div className="cartao-acesso__marca">
          <Marca tamanho={32} />
          <span>Nexital</span>
        </div>
        <h1>Acesso mediante convite</h1>
        <p className="cartao-acesso__subtitulo">
          O Nexital é usado por empresas convidadas{google ? <>, e <strong>{google.email}</strong> ainda não tem um convite</> : ''}. Peça o acesso a quem
          administra o Nexital na sua empresa, escreva para <a href={`mailto:${SUPORTE}`}>{SUPORTE}</a> ou deixe seus dados que entraremos em contato.
        </p>
        <Campo rotulo="Nome" valor={nome} mudar={mudar('nome')} autoComplete="name" />
        <Campo rotulo={`E-mail${google ? ' (confirmado pelo Google)' : ''}`} valor={email} mudar={mudar('email')} autoComplete="email" tipo="email" fixo={Boolean(google)} />
        <Campo rotulo="Telefone" valor={telefone} mudar={mudar('telefone')} autoComplete="tel" tipo="tel" />
        <Campo rotulo="Empresa" valor={empresa} mudar={mudar('empresa')} autoComplete="organization" />
        {erro && (
          <p className="cartao-acesso__erro" role="alert">
            {erro}
          </p>
        )}
        <button type="submit" className="botao-primario cartao-acesso__botao" disabled={situacao === 'enviando' || !completo}>
          {situacao === 'enviando' && <Loader2 size={18} className="girando" aria-hidden />} Quero conhecer o Nexital
        </button>
      </form>
    </main>
  );
}

type PropsCampo = { rotulo: string; valor: string; mudar: (v: string) => void; autoComplete: string; tipo?: string; fixo?: boolean };

function Campo({ rotulo, valor, mudar, autoComplete, tipo = 'text', fixo }: PropsCampo) {
  return (
    <label className="campo">
      <span>{rotulo}</span>
      <input type={tipo} autoComplete={autoComplete} required readOnly={fixo} value={valor} onChange={(e) => mudar(e.target.value)} />
    </label>
  );
}

function PedidoRecebido({ nome }: { nome: string }) {
  return (
    <main className="tela-acesso">
      <section className="cartao-acesso cartao-acesso--aviso" aria-live="polite">
        <CheckCircle2 size={44} color="#1f9d55" aria-hidden />
        <h1>Pedido recebido</h1>
        <p className="cartao-acesso__subtitulo">Obrigado, {nome.split(' ')[0]}. Vamos falar com você em breve pelo e-mail ou telefone informados.</p>
      </section>
    </main>
  );
}
